import { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, ExternalLink } from 'lucide-react';
import { useFlowStore } from '../stores/flowStore';
import { getFormUrl } from '../config/appConfig';

interface FlowSelectSearchableProps {
  value: FlowOption | null;
  onChange: (value: FlowOption | null) => void;
  placeholder?: string;
  error?: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface FlowOption {
  id: string;
  title: string;
  description?: string;
  status: string;
  publicUrl: string;
}

export function FlowSelectSearchable({
  value,
  onChange,
  placeholder = 'Select a flow...',
  error,
  label,
  description,
  disabled = false,
}: FlowSelectSearchableProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFlow, setSelectedFlow] = useState<FlowOption | null>(value);
  const [filteredFlows, setFilteredFlows] = useState<FlowOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { flows, loadFlows } = useFlowStore();

  // Keep closed-button label in sync when parent form resets / loads
  useEffect(() => {
    setSelectedFlow(value);
  }, [value]);

  // Load flows on component mount
  useEffect(() => {
    if (flows.length === 0) {
      setIsLoading(true);
      loadFlows().finally(() => setIsLoading(false));
    }
  }, [flows.length, loadFlows]);

  // Convert flows to options
  useEffect(() => {
    const flowOptions: FlowOption[] = flows
      .filter((flow) => flow.status === 'published') // Only show published flows
      .map((flow) => ({
        id: flow.id,
        title: flow.title || 'Untitled Flow',
        description: flow.description,
        status: flow.status,
        publicUrl: getFormUrl(flow.id, 'public'),
      }));

    setFilteredFlows(flowOptions);
  }, [flows]);

  // Filter flows based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredFlows(
        flows
          .filter((flow) => flow.status === 'published')
          .map((flow) => ({
            id: flow.id,
            title: flow.title || 'Untitled Flow',
            description: flow.description,
            status: flow.status,
            publicUrl: getFormUrl(flow.id, 'public'),
          }))
      );
    } else {
      const filtered = flows
        .filter(
          (flow) =>
            flow.status === 'published' &&
            (flow.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              flow.description
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase()))
        )
        .map((flow) => ({
          id: flow.id,
          title: flow.title || 'Untitled Flow',
          description: flow.description,
          status: flow.status,
          publicUrl: getFormUrl(flow.id, 'public'),
        }));

      setFilteredFlows(filtered);
    }
  }, [searchTerm, flows]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  const handleSelectFlow = (flow: FlowOption) => {
    onChange(flow);
    setSelectedFlow(flow);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleBlur = () => {
    // If typed value does not match selected flow, emit as new value
    if (!selectedFlow) {
      setSelectedFlow(null);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm text-gray-700 mb-2">
        {label}
      </label>

      <div className="relative">
        <button
          type="button"
          onClick={handleToggle}
          disabled={disabled}
          className={`w-full px-4 py-3 border rounded-lg transition-colors text-left flex items-center justify-between ${
            error ? 'border-red-300 bg-red-50' : 'border-gray-300'
          } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
        >
          <div className="flex-1 min-w-0">
            {selectedFlow && (
              <div className="flex-1 min-w-0">
                <div className="text-gray-900 truncate">
                  {selectedFlow.title}
                </div>
                {selectedFlow.description && (
                  <div className="text-sm text-gray-500 truncate">
                    {selectedFlow.description}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 ml-2">
            <ChevronDown
              className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </button>
      </div>

      {description && (
        <p className="mt-1 text-sm text-gray-500">{description}</p>
      )}

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onBlur={handleBlur}
                placeholder={placeholder}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                Loading flows...
              </div>
            ) : filteredFlows.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                {searchTerm
                  ? 'No flows found matching your search'
                  : 'No published flows available'}
              </div>
            ) : (
              filteredFlows.map((flow) => (
                <button
                  key={flow.id}
                  type="button"
                  onClick={() => handleSelectFlow(flow)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {flow.title}
                      </div>
                      {flow.description && (
                        <div className="text-sm text-gray-500 truncate mt-1">
                          {flow.description}
                        </div>
                      )}
                      <div className="text-xs text-gray-400 mt-1">
                        {flow.publicUrl}
                      </div>
                    </div>
                    <div className="ml-2 flex-shrink-0">
                      <ExternalLink
                        className="w-4 h-4 p-1 text-gray-400 hover:text-gray-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(flow.publicUrl, '_blank');
                        }}
                      />
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
